import type { NavLink, VerticalNavItemsType } from '@/@core/layouts/types'
import authConfig from '@/configs/auth'
import type { Page, Product2, Role, User } from '@/types/directus/current-user'

export enum SidenavActionType {
  SYNC_USER_ME = 'SYNC_USER_ME',
  SELECT_ROLE = 'SELECT_ROLE',
  RESET = 'RESET'
}

type ProductMenu = {
  id: string
  prefixes: string[]
  items: VerticalNavItemsType
  isDefault: boolean
}

export type SidenavState = {
  user: User | null
  roleOptions: Role[]
  selectedRoleId: string | null
  products: ProductMenu[]
}

export type SidenavAction =
  | { type: SidenavActionType.SYNC_USER_ME; payload: { user: User; selectedRoleId: string | null } }
  | { type: SidenavActionType.SELECT_ROLE; payload: string | null }
  | { type: SidenavActionType.RESET }

export const sidenavInitialState: SidenavState = {
  user: null,
  roleOptions: [],
  selectedRoleId: null,
  products: []
}

const normalizeUrl = (url: string) => (!url || url.startsWith('/') || /^https?:\/\//i.test(url) ? url : `/${url}`)
const byOrder = (a: { order: number | null }, b: { order: number | null }) =>
  (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY)

function buildProductMenu(product: Product2, role: Role): ProductMenu {
  const isSuperAdmin = authConfig.superAdminRoleCodes.includes(role.code)
  const visible = (page: Page) =>
    page.status === 'published' &&
    page.type !== 'sub-page' &&
    (isSuperAdmin || page.capabilities?.some(c => c.role === role.id && c.page === page.id && c.status === 'published'))
  const pageLink = (page: Page): NavLink => ({
    title: page.name,
    path: page.is_external_src ? `/embed/${page.id}` : normalizeUrl(page.url),
    ...(page.icon ? { icon: page.icon } : {})
  })

  const modules = (product.modules ?? [])
    .filter(module => module.status === 'published')
    .map(module => ({
      order: module.order,
      item: {
        title: module.name,
        ...(module.icon ? { icon: module.icon } : {}),
        children: (module.clusters ?? [])
          .filter(cluster => cluster.status === 'published')
          .slice()
          .sort(byOrder)
          .flatMap(cluster => {
            const pages = (cluster.pages ?? []).filter(visible).sort(byOrder)
            return pages.length ? [{ sectionTitle: cluster.name }, ...pages.map(pageLink)] : []
          })
      }
    }))
    .filter(module => module.item.children.length)
  const generalPages = (product.generalPages ?? []).filter(visible).map(page => ({
    order: page.order,
    item: pageLink(page)
  }))
  const code = product.code?.toLowerCase()
  const prefixes = code ? [`/solutions/${code}`, `/${code}`] : []
  if (code === 'admin' || code === 'core') prefixes.push('/solutions/core', '/core')
  const pages = [
    ...(product.modules ?? []).flatMap(module => module.clusters ?? []).flatMap(cluster => cluster.pages ?? []),
    ...(product.generalPages ?? [])
  ]
  prefixes.push(...pages.filter(page => page.url).map(page => normalizeUrl(page.url)))

  return {
    id: product.id,
    prefixes,
    items: [...modules, ...generalPages].sort(byOrder).map(entry => entry.item),
    isDefault: product.status === 'published' || isSuperAdmin
  }
}

function syncUser(user: User, selectedRoleId: string | null): SidenavState {
  const roleOptions = (user.roles ?? []).map(role => ({ ...role, label: role.name }))
  const role = roleOptions.find(option => option.id === selectedRoleId) ?? roleOptions[0]
  return {
    user,
    roleOptions,
    selectedRoleId: role?.id ?? null,
    products: role ? (role.products ?? []).map(product => buildProductMenu(product, role)) : []
  }
}

export function sidenavReducer(state: SidenavState, action: SidenavAction): SidenavState {
  switch (action.type) {
    // Rebuild menus for every product whenever a user/me response arrives.
    case SidenavActionType.SYNC_USER_ME:
      return syncUser(action.payload.user, action.payload.selectedRoleId)
    // Rebuild access for the selected role, falling back if the role was revoked.
    case SidenavActionType.SELECT_ROLE:
      if (!state.user || action.payload === state.selectedRoleId) return state
      return syncUser(state.user, action.payload)
    // Clear all menus when the user session is cleared.
    case SidenavActionType.RESET:
      return sidenavInitialState
    default:
      return state
  }
}

export function selectSidenavItems(state: SidenavState, pathname: string): VerticalNavItemsType {
  const product =
    state.products.find(product =>
      product.prefixes.some(prefix => pathname === prefix || pathname.startsWith(`${prefix.replace(/\/$/, '')}/`))
    ) ??
    state.products.find(product => product.isDefault) ??
    state.products[0]
  return product?.items ?? emptyNavItems
}

const emptyNavItems: VerticalNavItemsType = []
